// Lesson_Trygonometria_Tozsamosci_TeoriaPrzyklady.js

import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import TheoryExamplesRunner from '../../components/TheoryExamplesRunner';

/** Delikatny separator przerywany */
function DashedSep({ margin = '0.75rem' }) {
  return (
    <hr
      aria-hidden="true"
      style={{
        border: 0,
        borderTop: '1px dashed',
        borderColor: 'var(--color-text)',
        opacity: 1,
        margin: `${margin} 0`,
      }}
    />
  );
}

export default function Lesson_Trygonometria_Tozsamosci_TeoriaPrzyklady() {
  const intro = (
    <div className="space-y-3">
      <p>
        W tym podtemacie pracujemy z <strong>tożsamościami trygonometrycznymi</strong>{' '}
        dla <strong>kąta ostrego</strong>, czyli{' '}
        <InlineMath math={'\\alpha \\in (0^\\circ, 90^\\circ)'} />. W tej sytuacji mamy:
      </p>

      <p>
        Dla kąta ostrego wartości <InlineMath math={'\\sin\\alpha'} />,{' '}
        <InlineMath math={'\\cos\\alpha'} /> i <InlineMath math={'\\tan\\alpha'} /> są dodatnie.
      </p>

      <div className="space-y-2">
        <p className="font-medium">Jedynka trygonometryczna (dla każdego kąta):</p>
        <div style={{ textAlign: 'center' }}>
          <BlockMath math={'\\sin^2\\alpha + \\cos^2\\alpha = 1'} />
        </div>

      </div>

      <div className="space-y-2">
        <p className="font-medium">Definicja tangensa:</p>
        <div style={{ textAlign: 'center' }}>
          <BlockMath math={
            '\\tan\\alpha = ' +
            '\\dfrac{\\sin\\alpha}{\\cos\\alpha}'
          } />
        </div>
        <p>
          Dzięki temu, znając jedną wartość (np.{' '}
          <InlineMath math={'\\sin\\alpha'} /> lub{' '}
          <InlineMath math={'\\cos\\alpha'} />), możemy obliczyć pozostałe funkcje
          trygonometryczne.
        </p>
      </div>

      <DashedSep />

      <p className="text-sm opacity-90">
        W przykładach poniżej robimy dokładnie to, co w zadaniach z tego podtematu:{' '}
        najpierw korzystamy z jedynki trygonometrycznej, żeby policzyć brakującą
        funkcję, a następnie wyznaczamy <InlineMath math={'\\tan\\alpha'} /> z definicji
        lub obliczamy <InlineMath math={'\\sin\\alpha'} /> i{' '}
        <InlineMath math={'\\cos\\alpha'} /> z podanego tangensa.
      </p>
    </div>
  );

  const examples = [
    // PRZYKŁAD 1 – dany sin, szukamy cos i tan (odpowiednik GivenSinFindCosTanTask, wariant I)
    {
      label: 'Przykład 1',
      summary: 'Dany sinus kąta ostrego — obliczamy cosinus i tangens',
      description:
        'Najpierw korzystamy z jedynki trygonometrycznej, a następnie z definicji tangensa.',
      content: (
        <div className="space-y-3">
          <p>
            <strong>Polecenie:</strong> Dany jest{' '}
            <InlineMath math={'\\sin\\alpha = \\dfrac{3}{5}'} /> oraz wiadomo, że{' '}
            <InlineMath math={'\\alpha \\in (0^\\circ, 90^\\circ)'} />. Oblicz{' '}
            <InlineMath math={'\\cos\\alpha'} /> oraz{' '}
            <InlineMath math={'\\tan\\alpha'} />.
          </p>

          <p>
            <strong>Krok 1.</strong> Korzystamy z jedynki trygonometrycznej:
          </p>
          <div style={{ textAlign: 'center' }}>
            <BlockMath math={'\\sin^2\\alpha + \\cos^2\\alpha = 1'} />
            <BlockMath math={'\\cos^2\\alpha = 1 - \\sin^2\\alpha'} />
            <BlockMath math={
              '\\cos^2\\alpha = 1 - \\left(\\dfrac{3}{5}\\right)^2 ' +
              '= 1 - \\dfrac{9}{25} = \\dfrac{16}{25}'
            } />
          </div>

          <p>
            Dla kąta ostrego <InlineMath math={'\\cos\\alpha > 0'} />, więc:
          </p>
          <div style={{ textAlign: 'center' }}>
            <BlockMath math={'\\cos\\alpha = \\sqrt{\\dfrac{16}{25}} = \\dfrac{4}{5}'} />
          </div>

          <p>
            <strong>Krok 2.</strong> Korzystamy z definicji tangensa:
          </p>
          <div style={{ textAlign: 'center' }}>
            <BlockMath math={
              '\\tan\\alpha = \\dfrac{\\sin\\alpha}{\\cos\\alpha} = ' +
              '\\dfrac{\\tfrac{3}{5}}{\\tfrac{4}{5}} = \\dfrac{3}{4}'
            } />
          </div>

          <p>
            <strong>Wniosek:</strong>{' '}
            <InlineMath math={'\\cos\\alpha = \\dfrac{4}{5}'} /> oraz{' '}
            <InlineMath math={'\\tan\\alpha = \\dfrac{3}{4}'} />.
          </p>
        </div>
      ),
    },

    // PRZYKŁAD 2 – dany cos, szukamy sin i tan (GivenCosFindSinTanTask, wariant I)
    {
      label: 'Przykład 2',
      summary: 'Dany cosinus kąta ostrego — obliczamy sinus i tangens',
      description:
        'Z jedynki trygonometrycznej wyznaczamy sinus, a następnie obliczamy tangens.',
      content: (
        <div className="space-y-3">
          <p>
            <strong>Polecenie:</strong> Dany jest{' '}
            <InlineMath math={'\\cos\\alpha = \\dfrac{5}{13}'} /> oraz{' '}
            <InlineMath math={'\\alpha \\in (0^\\circ, 90^\\circ)'} />. Oblicz{' '}
            <InlineMath math={'\\sin\\alpha'} /> oraz{' '}
            <InlineMath math={'\\tan\\alpha'} />.
          </p>

          <p>
            <strong>Krok 1.</strong> Jedynka trygonometryczna:
          </p>
          <div style={{ textAlign: 'center' }}>
            <BlockMath math={'\\sin^2\\alpha + \\cos^2\\alpha = 1'} />
            <BlockMath math={'\\sin^2\\alpha = 1 - \\cos^2\\alpha'} />
            <BlockMath math={
              '\\sin^2\\alpha = 1 - \\left(\\dfrac{5}{13}\\right)^2 ' +
              '= 1 - \\dfrac{25}{169} = \\dfrac{144}{169}'
            } />
          </div>

          <p>
            Dla kąta ostrego <InlineMath math={'\\sin\\alpha > 0'} />, więc:
          </p>
          <div style={{ textAlign: 'center' }}>
            <BlockMath math={'\\sin\\alpha = \\sqrt{\\dfrac{144}{169}} = \\dfrac{12}{13}'} />
          </div>

          <p>
            <strong>Krok 2.</strong> Korzystamy z definicji tangensa:
          </p>
          <div style={{ textAlign: 'center' }}>
            <BlockMath math={
              '\\tan\\alpha = \\dfrac{\\sin\\alpha}{\\cos\\alpha} = ' +
              '\\dfrac{\\tfrac{12}{13}}{\\tfrac{5}{13}} = \\dfrac{12}{5}'
            } />
          </div>

          <p>
            <strong>Wniosek:</strong>{' '}
            <InlineMath math={'\\sin\\alpha = \\dfrac{12}{13}'} /> oraz{' '}
            <InlineMath math={'\\tan\\alpha = \\dfrac{12}{5}'} />.
          </p>
        </div>
      ),
    },

    // PRZYKŁAD 3 – dany tan, szukamy sin i cos (GivenTanFindSinCosTask, wariant I)
    {
      label: 'Przykład 3',
      summary: 'Dany tangens kąta ostrego — obliczamy sinus i cosinus',
      description:
        'Łączymy definicję tangensa z jedynką trygonometryczną, aby wyznaczyć pozostałe funkcje.',
      content: (
        <div className="space-y-3">
          <p>
            <strong>Polecenie:</strong> Dany jest{' '}
            <InlineMath math={'\\tan\\alpha = \\dfrac{3}{4}'} /> oraz{' '}
            <InlineMath math={'\\alpha \\in (0^\\circ, 90^\\circ)'} />. Oblicz{' '}
            <InlineMath math={'\\sin\\alpha'} /> oraz{' '}
            <InlineMath math={'\\cos\\alpha'} />.
          </p>

          <p>
            <strong>Krok 1.</strong> Z definicji tangensa:
          </p>
          <div style={{ textAlign: 'center' }}>
            <BlockMath math={'\\tan\\alpha = \\dfrac{\\sin\\alpha}{\\cos\\alpha}'} />
            <BlockMath math={
              '\\dfrac{3}{4} = \\dfrac{\\sin\\alpha}{\\cos\\alpha} ' +
              '\\quad\\Rightarrow\\quad \\sin\\alpha = \\dfrac{3}{4}\\cos\\alpha'
            } />
          </div>

          <p>
            <strong>Krok 2.</strong> Podstawiamy do jedynki trygonometrycznej:
          </p>
          <div style={{ textAlign: 'center' }}>
            <BlockMath math={'\\sin^2\\alpha + \\cos^2\\alpha = 1'} />
            <BlockMath math={
              '\\left(\\dfrac{3}{4}\\cos\\alpha\\right)^2 + \\cos^2\\alpha = 1'
            } />
            <BlockMath math={
              '\\dfrac{9}{16}\\cos^2\\alpha + \\cos^2\\alpha = 1'
            } />
            <BlockMath math={
              '\\dfrac{25}{16}\\cos^2\\alpha = 1'
            } />
            <BlockMath math={
              '\\cos^2\\alpha = \\dfrac{16}{25}'
            } />
          </div>

          <p>
            Dla kąta ostrego <InlineMath math={'\\cos\\alpha > 0'} />, więc:
          </p>
          <div style={{ textAlign: 'center' }}>
            <BlockMath math={'\\cos\\alpha = \\dfrac{4}{5}'} />
          </div>

          <p>
            <strong>Krok 3.</strong> Wracamy do zależności{' '}
            <InlineMath math={'\\sin\\alpha = \\dfrac{3}{4}\\cos\\alpha'} />:
          </p>
          <div style={{ textAlign: 'center' }}>
            <BlockMath math={
              '\\sin\\alpha = \\dfrac{3}{4} \\cdot \\dfrac{4}{5} = \\dfrac{3}{5}'
            } />
          </div>

          <p>
            <strong>Wniosek:</strong>{' '}
            <InlineMath math={'\\sin\\alpha = \\dfrac{3}{5}'} /> oraz{' '}
            <InlineMath math={'\\cos\\alpha = \\dfrac{4}{5}'} />.
          </p>
        </div>
      ),
    },
  ];

  return <TheoryExamplesRunner intro={intro} examples={examples} />;
}
